import { Slot, SlotManager } from "./../models/calendar.model";
import { Injectable } from "@angular/core";
import { UnavailableSlotDTO } from "@shared/models/unavailable-slot-dto";
import moment from "moment";
import { CarWorkshopCalendarPeriod } from "../models/calendar.model";
import { PeriodEnum } from "@shared/enums/period.enum";
import { AgendaSlotType } from "@shared/enums/agenda-slot-type.enum";
import { BehaviorSubject } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class CarWorkshopCalendarService {
  morningPeriod: CarWorkshopCalendarPeriod;
  afternoonPeriod: CarWorkshopCalendarPeriod;
  numberOfVehiclesPerPeriod: number;
  slotsMaganer: SlotManager = {};
  slotDuration = new BehaviorSubject<string>("00:30:00");

  constructor() {
    this.setMorningPeriod({ startTime: "08:00:00", endTime: "11:00:00" });
    this.setAfternoonPeriod({ startTime: "13:30:00", endTime: "17:00:00" });
  }

  setAfternoonPeriod(period: CarWorkshopCalendarPeriod) {
    this.afternoonPeriod = period;
  }

  setMorningPeriod(period: CarWorkshopCalendarPeriod) {
    this.morningPeriod = period;
  }

  setNumberOfVehiclesPerPeriod(numberOfVehiclesPerPeriod: number) {
    this.numberOfVehiclesPerPeriod = numberOfVehiclesPerPeriod;
  }

  generateMorningSlotTimes() {
    return this.generateSlotTimes(
      this.morningPeriod,
      this.numberOfVehiclesPerPeriod
    );
  }

  generateAfternoonSlotTimes() {
    return this.generateSlotTimes(
      this.afternoonPeriod,
      this.numberOfVehiclesPerPeriod
    );
  }

  /**
   * Generate the times of slots.
   *
   * For the default morning period, for example, it will return a array with two objects:
   * [{
   *  startTime: "08:00:00",
   *  endTime: "09:30:00"
   * }, {
   *  startTime: "09:30:00",
   *  endTime: "11:00:00"
   * }]
   *
   * @param period
   * @returns
   */
  generateSlotTimes(
    period: CarWorkshopCalendarPeriod,
    vehiclesPerPeriod: number
  ) {
    const startDate = moment(period.startTime, "HH:mm:ss");
    const minutesPerEvent = this.getMinutesPerEvent(period, vehiclesPerPeriod);

    this.slotDuration.next(
      moment("00:00:00", "HH:mm:ss")
        .add(generateSlotDurationValueInMinutes(minutesPerEvent), "m")
        .format("HH:mm:ss")
    );

    const events: CarWorkshopCalendarPeriod[] = [];
    for (let i = 0; i < vehiclesPerPeriod; i++) {
      events.push({
        startTime: startDate
          .clone()
          .add(minutesPerEvent * i, "m")
          .format("HH:mm:ss"),
        endTime: startDate
          .clone()
          .add(minutesPerEvent * (i + 1), "m")
          .format("HH:mm:ss")
      });
    }

    return events;
  }

  /**
   * Get the number of minutes of each event.
   * @returns The time in minutes for each slot.
   */
  getMinutesPerEvent(
    period: CarWorkshopCalendarPeriod,
    vehiclesPerPeriod: number
  ) {
    const startDate = moment(period.startTime, "HH:mm:ss");
    const endDate = moment(period.endTime, "HH:mm:ss");

    const totalTimeInMinutes = moment
      .duration(endDate.diff(startDate))
      .as("minutes");

    return totalTimeInMinutes / vehiclesPerPeriod;
  }

  /**
   * Generate the slot manager array with all slots set as available.
   *
   * @param startDate Format YYYY-MM-DD
   * @param endDate Format YYYY-MM-DD
   */
  setSlotManager(startDate: string, endDate: string) {
    const startDateM = moment(startDate, "YYYY-MM-DD");
    const endDateM = moment(endDate, "YYYY-MM-DD");

    const diff = Math.abs(endDateM.diff(startDateM, "days"));

    for (let i = 0; i <= diff; i++) {
      const date = startDateM.clone().add(i, "d");
      this.slotsMaganer[date.format("YYYY-MM-DD")] = {
        [PeriodEnum.MORNING]: this.generateSlots(
          this.generateMorningSlotTimes()
        ),
        [PeriodEnum.AFTERNOON]: this.generateSlots(
          this.generateAfternoonSlotTimes()
        )
      };
    }
  }

  /**
   * Given an array with times, generates new slots.
   * @param times A array with times.
   * @returns The generated slots.
   */
  generateSlots(times: CarWorkshopCalendarPeriod[]) {
    const slots: Slot[] = [];
    times.forEach((period) => {
      const slot: Slot = {
        startTime: period.startTime,
        endTime: period.endTime,
        type: AgendaSlotType.AVAILABLE
      };
      slots.push(slot);
    });
    return slots;
  }

  replaceAvailableSlotByUnavailableSlot(unavailableSlot: UnavailableSlotDTO) {
    const slotsPerDay = this.slotsMaganer[unavailableSlot.date];
    if (!slotsPerDay) return;

    const slots = slotsPerDay[unavailableSlot.period];
    if (!slots) return;

    const slot = slots.find((slot) => slot.type === AgendaSlotType.AVAILABLE);
    if (!slot) return;

    slot.type = unavailableSlot.type;
    slot.unavailableSlot = unavailableSlot;
    return slot;
  }

  /**
   * Repopulates slot manager slots with requests already created.
   * This is required when the number of requests is greater than the number of vehicles per period.
   */
  replaceSlotManagerOnlyRequests(
    date: string | Date,
    period: PeriodEnum,
    unavailableSlots: UnavailableSlotDTO[]
  ) {
    const slots: Slot[] = [];
    const times = this.generateSlotTimes(
      period === PeriodEnum.MORNING ? this.morningPeriod : this.afternoonPeriod,
      unavailableSlots.length
    );

    times.forEach((period, index) => {
      const slot: Slot = {
        startTime: period.startTime,
        endTime: period.endTime,
        type: AgendaSlotType.RESERVED,
        unavailableSlot: unavailableSlots[index]
      };
      slots.push(slot);
    });

    this.slotsMaganer[dateToYearMonthDayFormat(date)][period] = slots;
    return slots;
  }
}

/**
 * @param minutesPerEvent The minutes per event.
 * @returns The duration of the calendar's slot in minutes. It's used the multiples of 60 (30, 20, 15 and 10).
 */
const generateSlotDurationValueInMinutes = (minutesPerEvent: number) => {
  if (minutesPerEvent >= 30) return 30;
  if (minutesPerEvent < 30 && minutesPerEvent >= 20) return 20;
  if (minutesPerEvent < 20 && minutesPerEvent >= 15) return 15;
  return 10;
};

export const dateToYearMonthDayFormat = (date: string | Date) =>
  moment(date, "YYYY-MM-DD").format("YYYY-MM-DD");
